import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Card } from './Card';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackMessage?: string;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Ошибка рендера компонента:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  public override render() {
    if (this.state.hasError) {
      return (
        <Card className="border-red-500/30 bg-red-500/5 p-6 text-center">
          <div className="flex flex-col items-center justify-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/10 text-brand-red">
              <AlertTriangle size={20} />
            </div>
            <div className="space-y-1">
              <h3 className="text-sm font-semibold text-slate-200">
                {this.props.fallbackTitle ?? 'Произошла ошибка при отображении блока'}
              </h3>
              <p className="text-xs text-slate-400">
                {this.props.fallbackMessage ?? 'Не удалось отобразить данные'}
              </p>
              {this.state.error?.message && (
                <p className="mt-1 font-mono text-[11px] text-red-400/80">
                  {this.state.error.message}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={this.handleReset}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-surface-border bg-surface-overlay px-3 py-1.5 text-xs font-medium text-slate-300 transition hover:bg-white/5 hover:text-slate-100"
            >
              <RotateCcw size={13} />
              Попробовать снова
            </button>
          </div>
        </Card>
      );
    }

    return this.props.children;
  }
}
